import { useEffect } from "react";
import { keepLocked, randomPalette } from "@/color/generate";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { pinCurrentAsB, save, saveAs } from "../actions";
import { PREVIEW_TABS } from "../previews/stage";

function typing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
    el.getAttribute("role") === "slider"
  );
}

export function useHotkeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      const e2 = useEditor.getState();
      const ui = useUi.getState();
      const key = e.key.toLowerCase();
      if (mod && key === "k") {
        e.preventDefault();
        ui.setPalette(!ui.palette);
        return;
      }
      if (mod && key === "s") {
        e.preventDefault();
        void (e.shiftKey ? saveAs() : save());
        return;
      }
      if (mod && key === "o") {
        e.preventDefault();
        ui.openDialog("presets");
        return;
      }
      if (mod && key === "z" && !typing(e.target)) {
        e.preventDefault();
        e.shiftKey ? e2.redo() : e2.undo();
        return;
      }
      if (mod && key === "y" && !typing(e.target)) {
        e.preventDefault();
        e2.redo();
        return;
      }
      if (mod || e.altKey || typing(e.target) || ui.dialog || ui.palette) return;
      if (e.key === "\\") e2.toggleCompare();
      else if (key === "e") ui.openDialog("export");
      else if (key === "i") e2.set({ inspect: !e2.inspect });
      else if (key === "b") pinCurrentAsB();
      else if (key === "g") ui.openTool(ui.tool === "generate" ? null : "generate");
      else if (key === "a") ui.openTool(ui.tool === "adjust" ? null : "adjust");
      else if (e.key === "?") ui.openDialog("shortcuts");
      else if (e.key === " ")
        e2.edit((t) => ({
          ...t,
          palette: keepLocked(t.palette, randomPalette(t.variant), t.locks),
        }));
      else if (/^[1-8]$/.test(e.key))
        e2.set({ previewTab: PREVIEW_TABS[Number(e.key) - 1]?.id ?? "workspace" });
      else if (e.key === "Escape") {
        if (ui.tool) ui.openTool(null);
        else if (e2.inspect) e2.set({ inspect: false });
        else return;
      } else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
